import moment from "moment";
import qs from "qs";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { MgmtOfficerType } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { MediaFile, Modal, UnionCmdType } from "src/api/public-types";
import { postConnectedInfoListAsync } from "src/api/question/qeustion-api";
import { ConnectedInfo, ConnectedType, QuestionConnectedInfoListRequest } from "src/api/question/question-type";
import {
  WorkOrderDetailModel,
  WorkOrderFormType,
  WorkOrderPreivew,
  WorkOrderSheetCost,
  WorkOrderSheetCostType,
} from "src/api/work-order/workorder-types";
import { BaseButton, BaseModal } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import EditCancelButton from "src/components/EditCancelButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import SelectCsListPopup from "src/components/selectPopup/SelectCsListPopup";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import useGetPermissionAcGroup from "src/pages/visitor/hooks/useGetPermissionAcGroup";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import { YmdFormat } from "src/utils";
import useWorkOrder from "../../hooks/useWorkOrder";
import { makeRequestEditData } from "../../workOrder-function";
import { UnionRecurrenceType, WORK_TYPES } from "../../workOrder-types";
import WorkInfoSection from "./components/WorkInfoSection";
import WorkOrderAccessGroupSection from "./components/WorkOrderAccessGroupSection";
import WorkOrderEditSection from "./components/WorkOrderEditSection";
import WorkOrderScheduleSection from "./components/WorkOrderScheduleSection";

export interface Props {
  workOrderDetail?: WorkOrderDetailModel;
}

const CtrlWorkOrderForm = ({ workOrderDetail }: Props) => {
  const [buttonRef, setButtonRef] = useState<RefObject<HTMLButtonElement>>();
  const navigate = useNavigate();
  const { isAuthority } = usePartnerAuthority();
  const location = useLocation();
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );

  // 워크오더 ID
  const workOrderId = useMemo(() => {
    return queryParams?.id ? String(queryParams.id) : undefined;
  }, [queryParams.id]);

  // 토스트
  const { openToast } = useToast();

  const { setBaseModal, setAbstractModalZ1 } = useModal();

  const [groupPayload, setGroupPayload] = useState<{ isHoliday: boolean; recurrenceType: UnionRecurrenceType; recurrenceDayOfWeekType: string }>({
    isHoliday: true,
    recurrenceType: "",
    recurrenceDayOfWeekType: "",
  });

  const [scheduleOptions, setScheduleOptions] = useState(WORK_TYPES);

  const [allCancel, setAllCancel] = useState(false);
  // ============================

  // 경고창 팝업
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });

  // 관리처 담당자 리스트
  const [mgmtOfficerList, setMgmtOfficerList] = useState<Array<MgmtOfficerType>>([]);

  // 미리보기 이미지
  const [previewList, setPreviewList] = useState<WorkOrderPreivew[]>([]);
  // API 종류BaseRadio

  // 워크오더 업무내용 이미지
  const [workOrderMedias, setWorkOrderMedias] = useState<Array<MediaFile>>([]);

  // 워크오더 처리내용 이미지
  const [workOrderCompletedMedias, setWorkOrderCompletedMedias] = useState<Array<MediaFile>>([]);

  // 워크오더 처리비용 이미지
  const [workOrderCostMedias, setWorkOrderCostMedias] = useState<Array<MediaFile>>([]);

  // 상담관리 연결정보 등록/삭제
  const { executeAsync: postConnectedInfoList } = useApiOperation(postConnectedInfoListAsync, { noErrorModal: true });
  // 연결된 CS 정보
  const [connectedCs, setConnectedCs] = useState<ConnectedInfo[]>([]);
  // 수정 모드에서 기존 연결 정보 저장 (비교용)
  const [originalConnectedCs, setOriginalConnectedCs] = useState<ConnectedInfo[]>([]);

  // 컨트롤룸 baseUrl
  const ctrlUrl = useMemo(() => process.env.REACT_APP_BASEURL, []);

  const {
    defaultValues,
    editWorkOrder, // 워크오더 수정 api
    onInviteVisitor, // 방문자초대
    onDeleteVisitor, // 방문자삭제
    getBuildingOfficerInfo, // 건물 관리처 담당자 조회
    getWorkOrderPreviewList, // 워크오더 반복일정 조회
    createWorkOrder, //워크오더 생성
    createWorkOrderGroup, //워크오더 생성
    allCancelWorkOrderAndVisitor,
    retryInviteVisitor,
    getConnectedInfoList,
  } = useWorkOrder();

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    register,
    resetField,
    reset,
    getValues,
    formState: { errors },
  } = useForm<WorkOrderFormType>({
    defaultValues,
    mode: "onChange",
  });

  const formProps = {
    control,
    setValue,
    watch,
    getValues,
    errors,
    reset,
    resetField,
  };

  // 워크오더에 연결한 신청계약 > 기본권한 / 커스텀 권한 확인 > visitorDefaultAccessGroupYn 가 true 면 기본권한
  const { visitorDefaultAccessGroupYn } = useGetPermissionAcGroup(Number(watch("workOrderSheetAccessZoneList.0.contractManageId")));

  useEffect(() => {
    const _merged = [...workOrderMedias, ...workOrderCostMedias, ...workOrderCompletedMedias];
    setValue("mediaList", _merged);
  }, [workOrderCompletedMedias, workOrderCostMedias, workOrderMedias]);

  // 날짜 기간
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const isPast = useMemo(() => {
    const start = watch("workOrderSheetAccessZoneList.0.startDate") || null;
    const end = watch("workOrderSheetAccessZoneList.0.endDate") || null;

    const workDate = [start, end];

    if (workDate.every((date) => date !== null)) {
      const isPastStart = moment(workDate[0]).isBefore(moment().format(YmdFormat.YYYY_MM_DD));
      const isPastEnd = moment(workDate[1]).isBefore(moment().format(YmdFormat.YYYY_MM_DD));

      if (isPastStart || isPastEnd) return true;
    }
    return false;
  }, [dateRange]);

  const editData = makeRequestEditData(workOrderDetail!, watch());

  const getOfficersAndOfficName = async (mgmtOfficeId: string) => {
    const { mgmtBuildingName, officerList } = await getBuildingOfficerInfo(mgmtOfficeId);
    if (mgmtOfficeId && officerList?.length === 0) {
      setAlertModal({ isOpen: true, message: "관리처에 담당자 정보가 없습니다.", type: "resetManagement" });
      return;
    }
    setValue("buildingManagement", mgmtBuildingName);
    // 담당자 팝업에 보여줄 담당자 목록
    setMgmtOfficerList(officerList || []);
  };

  const managementResetAndClick = () => {
    if (watch("mgmtOfficeId")) {
      resetField("mgmtOfficeId"); //
      setMgmtOfficerList([]);
      buttonRef?.current?.click();
      return;
    }
  };

  // 워크오더 상세 setValue
  const setWorkOrderData = () => {
    if (workOrderDetail) {
      let workOrderSheetCostList: Array<WorkOrderSheetCostType> = workOrderDetail.workOrderSheetCostList || [];

      if (workOrderSheetCostList && workOrderSheetCostList?.length === 0) {
        workOrderSheetCostList = [{ cost: 0, description: "", cmdType: "C" }];
      } else {
        workOrderSheetCostList = workOrderSheetCostList
          ?.sort((a: WorkOrderSheetCostType, b: WorkOrderSheetCostType) => Number(a.id) - Number(b.id))
          .map((item: WorkOrderSheetCost) => {
            return { ...item, apiId: item.id };
          }) as WorkOrderSheetCostType[];
      }

      // setValue
      for (const [name, value] of Object.entries(workOrderDetail)) {
        setValue(name as keyof WorkOrderFormType, value, {
          shouldDirty: true,
        });
        if (name === "workOrderSheetCostList") {
          if ((value as Array<WorkOrderSheetCostType>) && value.length === 0) {
            setValue("workOrderSheetCostList", [{ cost: 0, description: "", cmdType: "C" }]);
          } else {
            const costList = value
              .sort((a: WorkOrderSheetCostType, b: WorkOrderSheetCostType) => Number(a.id) - Number(b.id))
              .map((item: WorkOrderSheetCost) => {
                return { ...item, apiId: item.id };
              });
            setValue("workOrderSheetCostList", costList);
          }
        }
      }

      // 워크오더 이미지들 setState
      if (workOrderDetail.mediaList && workOrderDetail.mediaList?.length > 0) {
        const workMedias = workOrderDetail.mediaList?.filter((media) => media.mediaServiceType === "MEDIA_WORK_ORDER_SHEET") || []; //업무내용 이미지
        const workCompletedMedias = //처리내용 이미지
          workOrderDetail.mediaList?.filter((media) => media.mediaServiceType === "MEDIA_WORK_ORDER_SHEET_COMPLETED_REPORT") || [];

        const costMedias = workOrderDetail.mediaList?.filter((media) => media.mediaServiceType === "MEDIA_WORK_ORDER_SHEET_COST") || []; //처리비용 이미지

        setWorkOrderMedias(workMedias);
        setWorkOrderCompletedMedias(workCompletedMedias);
        setWorkOrderCostMedias(costMedias);
      }

      getOfficersAndOfficName(String(workOrderDetail.mgmtOfficeId || ""));
    }
  };
  useEffect(() => {
    setWorkOrderData();
  }, [workOrderDetail]);

  // 연결된 CS 조회 (수정 모드일 때만)
  useEffect(() => {
    if (!workOrderId || !workOrderDetail?.id) {
      return;
    }

    const fetchConnectedCs = async () => {
      const { data } = await getConnectedInfoList({ connectedId: workOrderDetail.id, connectedType: "CS_CONNECTED_WORKORDER" });

      const connectedInfoList = data.data.content || [];
      setConnectedCs(connectedInfoList);
      setOriginalConnectedCs(connectedInfoList);
    };
    fetchConnectedCs();
  }, [workOrderId, workOrderDetail?.id, getConnectedInfoList]);

  // 워크오더 ID가 있으면(수정) 상세 api 호출
  // 유효성 검사

  const formValidation = useCallback(() => {
    const requiredMessage = "필수입력 항목입니다";

    // 건물/관리처
    register("mgmtOfficeId", {
      required: { value: true, message: requiredMessage },
    });

    // 담당자
    register("mgmtOfficerId", {
      required: { value: true, message: requiredMessage },
    });
  }, [register, workOrderId, isPast]);

  /**
   * 생성 또는 삭제 할 connectedInfoList를 생성 후 리턴합니다.
   * 생성된 데이터가 없으면 [] 빈배열을 리턴합니다.
   */
  const createConnectedInfoRequestData = ({
    connectedId,
    connectedType,
    updatedArray,
    originalArray,
  }: {
    connectedId: string;
    connectedType: ConnectedType;
    updatedArray?: ConnectedInfo[];
    originalArray?: ConnectedInfo[];
  }): QuestionConnectedInfoListRequest["connectedInfoList"] => {
    const addedElements =
      updatedArray
        ?.filter((updatedItem) => (originalArray ? originalArray.every((originalItem) => originalItem.questionId !== updatedItem.questionId) : true))
        .map((data) => ({
          questionId: data.questionId,
          connectedType: connectedType,
          connectedId: connectedId,
          cmdType: "C" as UnionCmdType,
        })) ?? [];

    const removedElements =
      originalArray
        ?.filter((originalItem) => (updatedArray ? updatedArray.every((updatedItem) => updatedItem.questionId !== originalItem.questionId) : true))
        .map((data) => ({
          id: data.id,
          questionId: data.questionId,
          connectedType: connectedType,
          connectedId: connectedId,
          cmdType: "D" as UnionCmdType,
        })) ?? [];

    return [...addedElements, ...removedElements];
  };

  // 워크오더 수정
  const handleEditWorkOrder = async () => {
    // 날짜 변경 여부 확인
    const isDateChanged = editData.startDate || editData.endDate;
    // 담당자 변경 여부 확인
    const isOfficerChanged = editData.mgmtOfficerId;
    const workOrderSheetId = String(workOrderId);

    // 에러 수집용 배열
    const failedRequests: { action: string; error: any; data?: any }[] = [];

    try {
      // 1. 워크오더 수정 API 호출
      const { data } = await editWorkOrder({ id: String(workOrderId), workOrderSheet: editData });

      // 2. 날짜 변경 시 기존 방문자 삭제
      if (isDateChanged || isOfficerChanged) {
        try {
          await onDeleteVisitor(workOrderSheetId);
        } catch (error) {
          failedRequests.push({
            action: "deleteVisitor",
            error,
            data: { workOrderSheetId },
          });
          console.error("방문자 삭제 실패:", error);
        }

        const updatedWorkOrderData = [
          {
            ...workOrderDetail,
            ...editData,
            id: workOrderId,
          },
        ] as WorkOrderDetailModel[];

        await retryInviteVisitor(updatedWorkOrderData[0], mgmtOfficerList);
      }

      // 3. CS 연결 정보 등록/수정
      const updatedWorkOrderId = String(data.data.content[0]?.id);
      if (connectedCs || originalConnectedCs.length > 0) {
        const csReqBody = createConnectedInfoRequestData({
          connectedId: updatedWorkOrderId,
          connectedType: "CS_CONNECTED_WORKORDER",
          updatedArray: connectedCs,
          originalArray: originalConnectedCs,
        });

        if (csReqBody.length > 0) {
          try {
            await postConnectedInfoList(csReqBody);
          } catch (error) {
            failedRequests.push({
              action: "updateConnectedCs",
              error,
              data: { workOrderId: updatedWorkOrderId, csReqBody },
            });
            console.error("CS 연결 정보 수정 실패:", error);
          }
        }
      }

      // 실패한 요청이 있으면 콘솔에 출력
      if (failedRequests.length > 0) {
        console.log("일부 요청이 실패했습니다:", failedRequests);
      }
      openToast({ content: `정상적으로 수정되었습니다.` });
      navigate(`${PagePath.workOrder.authenticated.detail.replace(":id", updatedWorkOrderId)}`);
      setBaseModal({ isOpen: false });
    } catch (error) {
      console.error("워크오더 수정 실패:", error);
      // 워크오더 수정 자체가 실패한 경우도 에러 수집
      failedRequests.push({
        action: "editWorkOrder",
        error,
        data: { workOrderId, editData },
      });

      if (failedRequests.length > 0) {
        console.log("요청 실패 내역:", failedRequests);
      }
    }
  };

  //
  // 컨펌모달 저장 클릭시 등록/수정
  const onClickConfirmModal = async (passData: WorkOrderFormType) => {
    //
    // 그룹 워크오더 일괄 취소
    if (allCancel && watch("workOrderSheetGroupId")) {
      await allCancelWorkOrderAndVisitor(String(watch("workOrderSheetGroupId")));
      navigate(`${PagePath.workOrder.authenticated.detail.replace(":id", String(workOrderId))}`);
      return;
    }

    // 워크오더 수정
    if (workOrderId) {
      await handleEditWorkOrder();
      return;
    }

    // 신규생성 && 정기업무 일때
    if (!workOrderId) {
      let passWorkSheetData;
      if (scheduleOptions[1].checked) {
        const groupRequest = {
          workorderFormData: passData,
          groupData: groupPayload,
          previewList,
        };
        passWorkSheetData = await createWorkOrderGroup(groupRequest);
      } else {
        passWorkSheetData = await createWorkOrder([passData]);
      }

      if (passWorkSheetData && passWorkSheetData.length > 0) {
        const createdWorkOrderId = String(passWorkSheetData[0]?.id);

        // CS 연결 정보 등록
        if (connectedCs && connectedCs.length > 0) {
          const csReqBody: QuestionConnectedInfoListRequest["connectedInfoList"] = connectedCs.map((cs) => ({
            questionId: cs.questionId,
            connectedType: "CS_CONNECTED_WORKORDER",
            connectedId: createdWorkOrderId,
            cmdType: "C" as UnionCmdType,
          }));

          try {
            await postConnectedInfoList(csReqBody);
          } catch (error) {
            console.error("CS 연결 정보 등록 실패:", error);
          }
        }

        setBaseModal({ isOpen: false });
        openToast({ content: `정상적으로 등록 되었습니다.` });

        await onInviteVisitor(passWorkSheetData, mgmtOfficerList);
        navigate(`${PagePath.workOrder.authenticated.detail.replace(":id", createdWorkOrderId)}`);
      }
    }
  };

  useEffect(() => {
    formValidation();
  }, [formValidation]);
  // 유효성 검사 후 저장 실행

  // 반복설정 -> 반복주기 확인 api
  const setWorkOrderPreview = async (passParams: WorkOrderPreivew): Promise<number> => {
    const previewList = await getWorkOrderPreviewList(passParams);
    const filteredList = previewList.filter((item) => {
      const newStartDate = moment(item.startDate).format(YmdFormat.YYYY_MM_DD);
      const today = moment().format(YmdFormat.YYYY_MM_DD);
      return moment(newStartDate).isSameOrAfter(moment(today));
    });
    setPreviewList(filteredList);
    return filteredList.length;
  };

  const onSubmit = async (data: WorkOrderFormType, e?: any) => {
    e.preventDefault();

    // 계약의 권한이 커스텀 권한인데 출입그룹 추가 안되어있을때 경고 모달 노출
    // if (
    //   !workOrderId &&
    //   visitorDefaultAccessGroupYn === false &&
    //   data.workOrderSheetAccessZoneList?.find((item) => !item.accessGroupAddIdsList || item.accessGroupAddIdsList?.length === 0)
    // ) {
    //   setBaseModal({
    //     isOpen: true,
    //     title: "출입그룹을 추가해주세요.",
    //     onClick: () => {
    //       setBaseModal({ isOpen: false });
    //     },
    //     btnRightTitle: "확인",
    //   });
    //   return;
    // }

    // 업무 이미지 / 처리 이미지 / 증빙 이미지 한 배열로 합치기
    let requestData: WorkOrderFormType;
    let message = "";

    const newMediaList = data.mediaList?.map((media) => {
      if (!media.id) {
        return {
          ...media,
          cmdType: "C",
        };
      } else {
      }
      return media;
    });

    requestData = { ...data, mediaList: newMediaList };

    if (workOrderId) {
      requestData = editData;
    } else {
      // 넘어온 객체 복사
      requestData = {
        ...requestData,
        workOrderSheetAccessZoneList: data.workOrderSheetAccessZoneList?.map((item) => {
          return {
            ...item, //
            startTime: moment(item.startTime).format("HH:mm"),
            endTime: moment(item.endTime).format("HH:mm"),
          };
        }),
      };
    }
    // 정기업무 등록시 CS 연결 메시지
    if (!scheduleOptions[0].checked && previewList.length > 0 && connectedCs && connectedCs.length > 0) {
      message = "정기적으로 생성한 모든 워크오더에 CS가 연결됩니다";
    }

    // 반복설정한 워크시트가 0개 일때는 생성 X -> 경고팝업
    if (!scheduleOptions[0].checked && previewList.length === 0) {
      setAlertModal({ isOpen: true, type: "recurrence" });
      return;
    }

    // CS 연결 정보 변경사항 체크 (수정 모드일 때만)
    const hasConnectedCsChanged =
      workOrderId &&
      (connectedCs.length !== originalConnectedCs.length ||
        connectedCs.some((cs) => !originalConnectedCs.some((original) => original.questionId === cs.questionId)) ||
        originalConnectedCs.some((original) => !connectedCs.some((cs) => cs.questionId === original.questionId)));

    if (!allCancel && Object.keys(requestData).length === 0 && !hasConnectedCsChanged) {
      setAlertModal({ isOpen: true, message: "변경사항 없이\n 저장할 수 없습니다." });

      return;
    }

    setBaseModal({
      isOpen: true,
      title: workOrderId ? "저장하시겠습니까?" : "워크오더를 생성하시겠습니까?",
      ...(message && { children: <p className="mt10 font14 text-primary3 no-wrap">{message}</p> }),
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
      onClick: () => {
        onClickConfirmModal(requestData);
        setBaseModal({ isOpen: false });
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
    });
  };

  // validation 통과하지 못하고 error 발생시 실행
  const onError = (errors: any, e?: any) => {
    console.log("onError errors", errors);

    return false;
  };

  return (
    <section>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="contents-container__scroll">
          <div className="contents-container__wrap ">
            <WorkInfoSection
              formProps={formProps}
              getOfficers={getOfficersAndOfficName}
              setButtonRef={setButtonRef}
              mgmtOfficerList={mgmtOfficerList}
              workOrderMedias={workOrderMedias || []}
              setWorkOrderMedias={setWorkOrderMedias}
              workOrderDetail={workOrderDetail}
            />
            <WorkOrderScheduleSection //
              formProps={formProps}
              isPast={isPast}
              mgmtOfficerList={mgmtOfficerList}
              scheduleOptions={scheduleOptions}
              allCancel={allCancel}
              previews={previewList}
              setPreviews={setPreviewList}
              setAllCancel={setAllCancel}
              setDateRange={setDateRange}
              setScheduleOptions={setScheduleOptions}
              setWorkOrderPreview={setWorkOrderPreview}
              setGroupPayload={setGroupPayload}
            />
            {!workOrderId && (
              <WorkOrderAccessGroupSection //
                contractManageId={Number(watch("workOrderSheetAccessZoneList.0.contractManageId"))}
                setAccessGroupIds={(ids: string[]) => {
                  setValue("workOrderSheetAccessZoneList.0.accessGroupAddIdsList", ids);
                }}
                visitorDefaultAccessGroupYn={visitorDefaultAccessGroupYn || false}
              />
            )}

            {/* 수정 시 노출되는 영역들 - 처리내용 / 처리 비용 / 고객답변 / 각 이미지 */}
            {workOrderId && (
              <WorkOrderEditSection
                formProps={formProps}
                workOrderCompletedMedias={workOrderCompletedMedias}
                setWorkOrderCompletedMedias={setWorkOrderCompletedMedias}
                workOrderCostMedias={workOrderCostMedias}
                setWorkOrderCostMedias={setWorkOrderCostMedias}
                workOrderDetail={watch()}
              />
            )}
            {/* 연결 정보 */}
            <article className="contents-container__wrap-article">
              <BaseSectionTitle title={"연결 정보"} />
              <section className="contents-container__grid ">
                <div className="contents-container__grid-index">
                  <p>CS</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="flex-row flex-center-start flex-wrap gap10">
                    <BaseButton
                      title="선택"
                      className="color-white"
                      onClick={() =>
                        setAbstractModalZ1({
                          isOpen: true,
                          size: "xlarge",
                          children: (
                            <SelectCsListPopup
                              defaultCheckedList={connectedCs?.map((cs) => cs.questionId) || []}
                              onClick={(selectedIds: string[]) => {
                                // 선택된 CS ID 리스트를 ConnectedInfo 형태로 변환하여 state에 저장
                                const selectedConnectedCs: ConnectedInfo[] = selectedIds.map((questionId) => ({
                                  id: "", // 등록 모드에서는 임시 ID
                                  questionId,
                                  connectedType: "CS_CONNECTED_WORKORDER",
                                  connectedId: workOrderId || "", // 등록 모드에서는 빈 문자열
                                }));
                                setConnectedCs(selectedConnectedCs);
                                setAbstractModalZ1({ isOpen: false });
                              }}
                              onClose={() => setAbstractModalZ1({ isOpen: false })}
                            />
                          ),
                        })
                      }
                    />

                    {connectedCs && connectedCs.length > 0
                      ? connectedCs?.map((data) => (
                          <div className="flex-row flex-center h-40" key={data.questionId}>
                            <BaseNewTabLink
                              path={PagePath.question.detail.replace(":id", data.questionId)}
                              value={data.questionId ?? "-"}
                              ellipsis
                              className=""
                            />
                            <div
                              className="delete-btn-x"
                              onClick={() => {
                                const filteredConnectedCs = connectedCs.filter((prevData) => prevData.questionId !== data.questionId);
                                setConnectedCs(filteredConnectedCs);
                              }}
                            ></div>
                          </div>
                        ))
                      : null}
                    {/* <BaseButton title="생성" className="color-white ml18" onClick={() => alert("워크오더를 생성해주세요")} /> */}
                  </div>
                  {((!workOrderId && !scheduleOptions[0].checked) || (workOrderId && workOrderDetail?.workOrderSheetGroupId)) &&
                    (!connectedCs || connectedCs.length === 0) && (
                      <ul className="base-list mt10">
                        <li>정기적으로 생성한 모든 워크오더에 CS가 연결됩니다.</li>
                      </ul>
                    )}
                </div>
              </section>
              <section className="contents-container__grid ">
                <div className="contents-container__grid-index">
                  <p>모바일웹 URL</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="flex-center-start flex-row">
                    <span className="font14 pre-formatted">
                      <a
                        className="text-underline "
                        href={`${ctrlUrl}/mng/ctrl/public/workOrder/${workOrderDetail?.workOrderSheetNo}`}
                        target="_blank"
                        rel="noreferrer"
                      >{`${ctrlUrl}/mng/ctrl/public/workOrder/${workOrderDetail?.workOrderSheetNo}`}</a>
                    </span>
                    <span className="ic-target-blank"></span>
                  </div>
                </div>
              </section>
            </article>
          </div>
        </div>

        {/* 버튼영역 */}
        <div className="contents-container__btn-wrap">
          <div className="left-area"></div>
          <div className="right-area">
            <>
              {workOrderDetail && Object.keys(editData).length !== 0 && (
                <div className="mr20">
                  <p className="required font14 text-red">수정된 사항이 있으니 다시 한번 확인해주세요.</p>
                </div>
              )}
              <EditCancelButton
                to={workOrderId ? PagePath.workOrder.authenticated.detail.replace(":id", String(workOrderId)) : PagePath.workOrder.authenticated.list}
              />
            </>

            {workOrderDetail?.status === "WORK_ORDER_CANCELED" ? (
              <p className="text-red900 font16">취소된 워크오더 입니다.</p>
            ) : workOrderDetail?.status === "WORK_ORDER_CLOSED" ? (
              <p className="text-red900 font16">종료된 워크오더 입니다.</p>
            ) : (
              (!watch("mediaList")?.length || isAuthority("w", "BASIC")) && <BaseButton type="submit" title="저장" className="size-large" />
            )}
          </div>
        </div>
      </form>
      <>
        {alertModal.isOpen && (
          <BaseModal
            isOpen={true}
            btnRightTitle="확인"
            onClick={() => {
              if (alertModal.type === "resetManagement") {
                managementResetAndClick();
              }
              setAlertModal({ isOpen: false });
            }}
          >
            {alertModal.message && <div className="font18 mt15 text-gray900 pre-formatted keep-all"> {alertModal.message}</div>}
            {alertModal.type === "recurrence" && (
              <div>
                <p className="font18  text-gray900">
                  <b>스케줄 미리보기를 먼저 해주세요</b>
                </p>
                {/* <p className="font14 mt20">반복 일정을 다시 설정해 주세요.</p> */}
                <p className="font14 mt20">
                  설정한 스케줄을 먼저 확인하신 후에 <br /> 업무를 생성할 수 있습니다.
                </p>
              </div>
            )}
          </BaseModal>
        )}
      </>
    </section>
  );
};

export default CtrlWorkOrderForm;
